import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { RelevanceBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Sparkles, Loader2, Brain, ExternalLink } from "lucide-react";

const PLATFORMS = ["", "CanadaBuys", "MERX", "BidsAndTenders", "Bonfire", "SAPAriba", "SEAO"];
const PROVINCES = ["", "ON", "QC", "BC", "AB", "SK", "MB", "NS", "NB", "NL", "PE"];

const FmtDate = ({ value }) => {
  if (!value) return <span className="text-zinc-400">—</span>;
  return (
    <span className="font-mono text-xs">
      {new Date(value).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })}
    </span>
  );
};

export default function AiFilter() {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState("HR, payroll, ERP, Oracle, SAP, K-12 related RFPs");
  const [platform, setPlatform] = useState("");
  const [province, setProvince] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");

  const runFilter = async () => {
    if (!criteria.trim()) return;
    setRunning(true);
    setError("");
    try {
      const res = await api.aiFilter({
        criteria: criteria.trim(),
        platform: platform || undefined,
        province: province || undefined,
        limit: 100,
        use_claude: true,
      });
      setResults(res.results || []);
      setTotal(res.total || 0);
    } catch (e) {
      setError(e?.message || "Filter failed");
      setResults([]);
    } finally {
      setRunning(false);
    }
  };

  const relevant = results.filter(r => r.ai_relevant);
  const nonRelevant = results.filter(r => !r.ai_relevant);

  return (
    <div className="p-8" data-testid="ai-filter-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
          AI-powered analysis
        </div>
        <h1 className="text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-3">
          <Brain size={28} className="text-[#002FA7]" />
          Filter with Claude
        </h1>
      </div>

      <div className="border border-zinc-200 bg-white p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-4 flex items-center gap-2">
          <Sparkles size={12} /> Claude AI Criteria
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
              What are you looking for?
            </label>
            <textarea
              value={criteria}
              onChange={e => setCriteria(e.target.value)}
              placeholder="Describe the type of RFPs you want…"
              rows={3}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent resize-none"
            />
            <p className="text-[11px] text-zinc-400 mt-1">
              Claude will analyze each RFP against your criteria and mark relevant ones.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1 block">Platform (optional)</label>
              <Select value={platform || "_all"} onValueChange={v => setPlatform(v === "_all" ? "" : v)}>
                <SelectTrigger className="rounded-sm border-zinc-300 text-sm h-9">
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p || "_all"} value={p || "_all"}>{p || "All platforms"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1 block">Province (optional)</label>
              <Select value={province || "_all"} onValueChange={v => setProvince(v === "_all" ? "" : v)}>
                <SelectTrigger className="rounded-sm border-zinc-300 text-sm h-9">
                  <SelectValue placeholder="All provinces" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCES.map(p => (
                    <SelectItem key={p || "_all"} value={p || "_all"}>{p || "All provinces"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={runFilter}
                disabled={running || !criteria.trim()}
                className="w-full rounded-sm bg-[#002FA7] hover:bg-[#002175] text-white h-9"
              >
                {running ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Analyzing…</>
                ) : (
                  <><Sparkles size={14} className="mr-1.5" /> Run AI Filter</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-700">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-zinc-600">
            <strong className="text-zinc-900">{relevant.length}</strong> relevant
            {" / "}
            <strong className="text-zinc-900">{nonRelevant.length}</strong> not relevant
            {" — "}
            <span className="text-zinc-400">{total} analyzed</span>
          </div>
          <div className="text-[11px] text-zinc-400 font-mono">
            Powered by Claude 3.5 Sonnet
          </div>
        </div>
      )}

      {relevant.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-zinc-800 mb-3 flex items-center gap-2">
            <Sparkles size={14} className="text-green-600" />
            Relevant Opportunities
          </h2>
          <div className="border border-zinc-200 divide-y divide-zinc-100">
            {relevant.map((r, i) => (
              <div
                key={r.id || i}
                className="flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/opportunities/${r.id}`)}
              >
                <div className="shrink-0 w-8 text-center">
                  <RelevanceBadge score={r.ai_score} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {r.rfp_title}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-zinc-500">{r.buyer_name}</span>
                    <span className="text-[10px] text-zinc-300">·</span>
                    <span className="text-[10px] font-mono text-zinc-400">{r.source_platform}</span>
                    {r.province && (
                      <>
                        <span className="text-[10px] text-zinc-300">·</span>
                        <span className="text-[10px] font-mono text-zinc-400">{r.province}</span>
                      </>
                    )}
                    {r.closing_deadline && (
                      <>
                        <span className="text-[10px] text-zinc-300">·</span>
                        <FmtDate value={r.closing_deadline} />
                      </>
                    )}
                  </div>
                  {r.ai_reason && (
                    <div className="text-[11px] text-zinc-500 mt-1 italic leading-relaxed">
                      {r.ai_reason}
                    </div>
                  )}
                </div>
                {r.source_url && (
                  <a
                    href={r.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 mt-1 text-zinc-300 hover:text-[#002FA7] transition-colors"
                    title="Open source URL"
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {nonRelevant.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-zinc-500 mb-3 flex items-center gap-2">
            Not Relevant
          </h2>
          <div className="border border-zinc-200 divide-y divide-zinc-100 opacity-70">
            {nonRelevant.slice(0, 10).map((r, i) => (
              <div
                key={r.id || i}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-zinc-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/opportunities/${r.id}`)}
              >
                <div className="shrink-0 w-8 text-center">
                  <RelevanceBadge score={r.ai_score} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-600 truncate">{r.rfp_title}</div>
                  <div className="text-[11px] text-zinc-400">{r.buyer_name}</div>
                </div>
                {r.source_url && (
                  <a
                    href={r.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 text-zinc-300 hover:text-[#002FA7] transition-colors"
                    title="Open source URL"
                  >
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
          {nonRelevant.length > 10 && (
            <p className="text-xs text-zinc-400 mt-2">…and {nonRelevant.length - 10} more</p>
          )}
        </div>
      )}

      {!running && results.length === 0 && !error && (
        <div className="text-center py-16 text-zinc-400">
          <Brain size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">Enter your criteria above and click <strong>Run AI Filter</strong></p>
          <p className="text-xs mt-1">Claude will analyze each RFP and highlight the ones that match your needs.</p>
        </div>
      )}

      {running && results.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <Loader2 size={24} className="mx-auto mb-3 animate-spin" />
          <p className="text-sm">Claude is analyzing your RFPs…</p>
        </div>
      )}
    </div>
  );
}
